/**
 * Confirm Schedule for one event, on its own page (migration 1780, scheduling delegates).
 *
 * A scheduling delegate who is an MSR does not get the full event page; this page shows the event's name, date and place
 * and its Confirm Schedule tab (the Sales Reps list, Assign Rep, and the dates x reps grid for multi-day events). The
 * database decides what they may change: reps of the team they schedule for, on that team's events.
 */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../services/supabase";
import SalesRepsTab from "../components/events/tabs/SalesRepsTab";
import LoadingSpinner from "../components/common/LoadingSpinner";

interface EventHeader {
  id: string;
  title: string;
  start_time: string | null;
  end_time: string | null;
  timezone: string | null;
  all_day: boolean | null;
  location_name: string | null;
  status: string | null;
}

function when(e: EventHeader): string {
  if (!e.start_time) {
    return "Date to be decided";
  }
  const timeZone = e.timezone || "America/Chicago";
  try {
    const day = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(e.start_time));
    if (e.all_day) {
      return day;
    }
    const t = (iso: string, zone: boolean) =>
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
        ...(zone ? { timeZoneName: "short" } : {}),
      }).format(new Date(iso));
    return `${day} · ${t(e.start_time, !e.end_time)}${e.end_time ? ` – ${t(e.end_time, true)}` : ""}`;
  } catch {
    return new Date(e.start_time).toLocaleString();
  }
}

const ScheduleEventConfirm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data: event,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["schedule-event-header", id],
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from("events")
        .select(
          "id, title, start_time, end_time, timezone, all_day, location_name, status",
        )
        .eq("id", id as string)
        .maybeSingle();
      if (err) {
        throw err;
      }
      return data as EventHeader | null;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !event) {
    return (
      <div className="p-6">
        <p className="text-gray-700">
          This event could not be found, or it is not one you schedule for.
        </p>
        <Link
          to="/sales-calendar"
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          Back to the Sales Calendar
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl">
      <div>
        <Link
          to="/sales-calendar"
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          ← Sales Calendar
        </Link>
        <h1
          className="mt-1 text-xl font-bold text-gray-900"
          data-testid="schedule-event-title"
        >
          {event.title}
        </h1>
        <p className="text-sm text-gray-600">
          {when(event)}
          {event.location_name ? ` · ${event.location_name}` : ""}
          {event.status && event.status !== "scheduled"
            ? ` · ${event.status}`
            : ""}
        </p>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Confirm Schedule
        </h2>
        <SalesRepsTab eventId={event.id} eventTitle={event.title} />
      </div>
    </div>
  );
};

export default ScheduleEventConfirm;
